import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO, subDays } from 'date-fns';
import useUpdateParkingServiceParkingRates from './useUpdateParkingServiceParkingRates.ts';
import { ParkingService } from '../../models/ParkingService.ts';
import {
  AdditionalRules,
  ParkingRate,
  RateDetail,
  RegularRule,
  SpecificRule,
} from '../../models/ParkingRate.ts';
import { notifyError } from '../../utils/NotifyError.tsx';
import { notifySuccess } from '../../utils/NotifySuccess.tsx';
import {
  REGULAR_MAX_END,
  filterRuleToEffectivePeriod,
  groupsToRegularRatePeriods,
  groupsToSpecificRatePeriods,
  maxFeeEntriesToRegularPeriods,
  maxFeeEntriesToSpecificPeriods,
  type MaxFeeRuleEntry,
} from '../../utils/ParkingRateConverters.ts';
import { dateToStr } from '../../utils/ParkingRateTime.ts';
import { type RateGroup } from './useParkingRateGroups.ts';

interface UseSaveParkingRatesParams {
  service?: ParkingService;
  serviceId?: string;
  localRuleType: 'regular' | 'specific';
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  description: string;
  entryBufferTime: string;
  isResetEveryDay: boolean;
  groups: RateGroup<RateDetail>[];
  regularDowSubset: number[];
  specialDates: string[];
  maxFeeEntries: MaxFeeRuleEntry[];
  ruleIndex?: number;
  isEditMode: boolean;
  navigateBack: (withReopen?: boolean) => void;
}

export function useSaveParkingRates({
  service,
  serviceId,
  localRuleType,
  name,
  startDate,
  endDate,
  description,
  entryBufferTime,
  isResetEveryDay,
  groups,
  regularDowSubset,
  specialDates,
  maxFeeEntries,
  ruleIndex,
  isEditMode,
  navigateBack,
}: UseSaveParkingRatesParams) {
  const { mutateAsync: updateParkingServiceParkingRates, isPending: isSaving } =
    useUpdateParkingServiceParkingRates();
  const queryClient = useQueryClient();

  const saveAll = async () => {
    if (!service || !startDate || (localRuleType === 'specific' && !endDate)) {
      notifyError('資料尚未載入完成，請稍後再試');
      return;
    }

    const startDateStr = dateToStr(startDate);
    const currentParkingRate: ParkingRate = service.parkingRate ?? {
      regularRules: [],
      specificRules: [],
    };
    let newParkingRate: ParkingRate;

    if (localRuleType === 'regular') {
      const newRule: RegularRule = {
        name: name.trim(),
        effectivePeriod: {
          startDateTime: `${startDateStr}T00:00:00`,
          endDateTime: `${REGULAR_MAX_END}T23:59:59`,
        },
        ratePeriods: groupsToRegularRatePeriods(groups, regularDowSubset),
        maximumFeeRules: maxFeeEntriesToRegularPeriods(maxFeeEntries),
        additionalRules: {
          entryBufferTime: entryBufferTime.trim()
            ? parseInt(entryBufferTime)
            : null,
          isResetEveryDay,
        } satisfies AdditionalRules,
        description: description.trim(),
      };
      const regularRules = [...(currentParkingRate.regularRules ?? [])];
      if (ruleIndex !== undefined) regularRules[ruleIndex] = newRule;
      else regularRules.push(newRule);

      // 所有一般費率按起始日排序，重算每一筆的結束日
      regularRules.sort((a, b) => {
        const aStart = a.effectivePeriod.startDateTime.split('T')[0];
        const bStart = b.effectivePeriod.startDateTime.split('T')[0];
        return aStart < bStart ? -1 : aStart > bStart ? 1 : 0;
      });
      for (let i = 0; i < regularRules.length; i++) {
        const nextStart =
          i + 1 < regularRules.length
            ? regularRules[i + 1].effectivePeriod.startDateTime.split('T')[0]
            : null;
        const endStr = nextStart
          ? format(subDays(parseISO(nextStart), 1), 'yyyy-MM-dd')
          : REGULAR_MAX_END;
        regularRules[i] = filterRuleToEffectivePeriod(regularRules[i], endStr);
      }
      newParkingRate = { ...currentParkingRate, regularRules };

      // 判斷是否需要兩階段更新：
      // - 新增模式：一定需要（新 rule 會與既有 rule 的現有期間重疊）
      // - 編輯模式且起始日有變動：需要（其他 rule 的 end date 要先縮短）
      // - 編輯模式且起始日不變：不需要（其他 rule 的 end date 不受影響）
      const oldStartDate =
        ruleIndex !== undefined
          ? (currentParkingRate.regularRules ?? [])[
              ruleIndex
            ]?.effectivePeriod.startDateTime.split('T')[0]
          : null;
      const startDateChanged =
        ruleIndex !== undefined && oldStartDate !== startDateStr;
      const needsTwoStepSave = ruleIndex === undefined || startDateChanged;

      if (needsTwoStepSave) {
        // Step 1：送出不含新增/異動 rule 的版本，讓後端先騰出空間
        const step1Rules = regularRules.filter(
          (r) => r.effectivePeriod.startDateTime.split('T')[0] !== startDateStr,
        );
        await updateParkingServiceParkingRates({
          serviceId: serviceId!,
          parkingRate: { ...currentParkingRate, regularRules: step1Rules },
        });
      }
    } else {
      const endDateStr = dateToStr(endDate!);
      const newRule: SpecificRule = {
        name: name.trim(),
        effectivePeriod: {
          startDateTime: `${startDateStr}T00:00:00`,
          endDateTime: `${endDateStr}T23:59:59`,
        },
        ratePeriods: groupsToSpecificRatePeriods(groups, specialDates),
        maximumFeeRules: maxFeeEntriesToSpecificPeriods(
          maxFeeEntries,
          specialDates,
        ),
        additionalRules: {
          entryBufferTime: entryBufferTime.trim()
            ? parseInt(entryBufferTime)
            : null,
          isResetEveryDay,
        } satisfies AdditionalRules,
        description: description.trim(),
      };
      const specificRules = [...(currentParkingRate.specificRules ?? [])];
      if (ruleIndex !== undefined) specificRules[ruleIndex] = newRule;
      else specificRules.push(newRule);
      newParkingRate = { ...currentParkingRate, specificRules };
    }

    try {
      await updateParkingServiceParkingRates({
        serviceId: serviceId!,
        parkingRate: newParkingRate,
      });
      await queryClient.invalidateQueries({
        queryKey: ['parkingServices', serviceId],
      });

      if (isEditMode) {
        notifySuccess('更新停車費率成功');
      } else {
        notifySuccess('新增停車費率成功');
      }
      navigateBack(true);
    } catch (error) {
      if (error.code === 'INVALID_INPUT') {
        notifyError('選定費率生效期間已有其他費率');
      } else {
        notifyError('儲存失敗', error.message);
      }
    }
  };

  return { saveAll, isSaving };
}
